import {
  revisionAgreement,
  useDeploymentIdentityQuery,
  webBuildIdentity,
  type DeploymentIdentity,
} from "@/api/deploymentIdentity";
import { useRuntimeConfig } from "@/config/RuntimeConfigContext";
import { Badge } from "@/ui/Badge";
import { Dialog, DialogContent } from "@/ui/Dialog";
import { ErrorState, LoadingState } from "@/ui/states";

/**
 * "What exactly am I looking at?", answered without a pod or a database (#490).
 *
 * The failure this closes: on 2026-08-24 Development served a three-day-old
 * bundle while `main` had advanced twice, and there was no way for QA to tell
 * from inside the application. Every screenshot from that window proved
 * nothing, because it could equally have been a feature that was never built.
 *
 * WHAT IT SHOWS AND WHERE EACH VALUE COMES FROM — the distinction is the whole
 * point of the panel and it is on screen, not only in this comment:
 *
 *   Web       from the bundle this browser is EXECUTING (baked at build time).
 *   Service   from the running service image (baked at build time).
 *   Declared  from `/config.json` — what the DEPLOYMENT says it rolled out.
 *
 * Evidence, evidence, claim. The summary at the top is the comparison, because
 * that is the question being asked: three values a reader has to diff by eye is
 * how "they look about the same" becomes an answer.
 *
 * NO SECRETS AND NO PHI: commit ids, image digests, an asset filename and
 * timestamps. Everything here is already public in the served bundle or in
 * `/config.json`; what is authenticated is the SURFACE, not the data — see
 * `services/editor-service/src/editor_service/api/browser/support.py`.
 */
export function DeploymentIdentityDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const config = useRuntimeConfig();
  // Only while the panel is open. This is a support surface, and putting a
  // request for it on every page load would make an occasional question into
  // permanent traffic.
  const query = useDeploymentIdentityQuery(open);
  const web = webBuildIdentity();
  const agreement = revisionAgreement(web, query.data?.service, config.release);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        title="Deployment details"
        description="The exact revisions this environment is running, and whether they agree."
      >
        <div className="space-y-4">
          <AgreementSummary agreement={agreement} />

          <Section title="Web (this browser)">
            <Row
              label="Source commit"
              value={web.sourceCommit}
              note="Baked into the bundle you are executing."
            />
            <Row label="Built" value={web.builtAt} />
            <Row
              label="Served asset"
              value={web.assetId ?? "not observable"}
              note="The file this page actually loaded."
            />
            {/* The image digest cannot come from the browser — a page has no way
                to know which image served it — so it is the deployment's
                declaration, fetched with the rest of the server-side answer and
                labelled as a claim. It sits HERE rather than in a block of its
                own because a reader asking "what is the web half running" wants
                one place to look. */}
            <Row
              label="Image digest"
              value={
                query.data?.web.imageDigest ??
                "declared by the deployment: none"
              }
              note="What the deployment says it deployed. The authoritative read is the kubelet's."
            />
          </Section>

          <Section title="Declared by the deployment">
            <Row
              label="Release commit"
              value={
                config.release?.deploymentCommit ??
                config.release?.commit ??
                "absent"
              }
              note="From /config.json — a claim, not evidence: it is written at deploy time."
            />
            <Row label="Environment" value={config.environment} />
          </Section>

          {query.isPending ? (
            <LoadingState
              label="Reading the service's deployment identity"
              rows={2}
            />
          ) : query.isError ? (
            <ErrorState
              title="The Editor Service did not answer"
              description="Its revision identity is unavailable, so only the browser half above is known."
              onRetry={() => void query.refetch()}
            />
          ) : (
            <ServiceSections identity={query.data} />
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function AgreementSummary({
  agreement,
}: {
  agreement: ReturnType<typeof revisionAgreement>;
}) {
  if (agreement === "agrees") {
    return (
      <p className="rounded-md border border-border bg-card p-3 text-sm">
        <Badge tone="success">Consistent</Badge>{" "}
        <span className="text-muted-foreground">
          The browser and the Editor Service match the build revisions recorded
          for this deployment.
        </span>
      </p>
    );
  }
  if (agreement === "mismatch") {
    // `role="alert"` because this is the finding, not a decoration: a reader who
    // opened this panel to check a screenshot needs to be told, not to notice.
    return (
      <p
        role="alert"
        className="rounded-md border border-destructive bg-destructive/10 p-3 text-sm"
      >
        <Badge tone="destructive">Revisions disagree</Badge>{" "}
        <span>
          This environment is not running one consistent revision. Evidence
          taken from it — screenshots included — cannot be attributed to a
          commit until it is redeployed.
        </span>
      </p>
    );
  }
  return (
    <p className="rounded-md border border-border bg-card p-3 text-sm">
      <Badge tone="warning">Not verifiable</Badge>{" "}
      <span className="text-muted-foreground">
        At least one half of this deployment does not carry a build identity, so
        nothing here confirms which commit is running.
      </span>
    </p>
  );
}

function ServiceSections({ identity }: { identity: DeploymentIdentity }) {
  const { service, engine, brain } = identity;
  return (
    <>
      <Section title="Editor Service">
        <Row
          label="Source commit"
          value={service.sourceCommit ?? "not carried by this image"}
          note={
            service.sourceCommitEvidence === "image-baked"
              ? "Baked into the running image."
              : "This image predates build-identity baking, so the value is not evidence."
          }
        />
        <Row
          label="Declared commit"
          value={service.declaredSourceCommit ?? "none"}
        />
        <Row label="Agreement" value={service.sourceCommitAgreement} />
        <Row label="Image digest" value={service.imageDigest ?? "unknown"} />
        <Row label="Version" value={service.version} />
        <Row label="Built" value={service.builtAt ?? "unknown"} />
        <Row
          label="Process started"
          value={service.startedAt}
          note="When this replica started — not when the rollout happened."
        />
      </Section>

      <Section title="Pack-validation engine">
        {engine.mounted ? (
          <>
            <Row
              label="Brain checkout"
              value={engine.brainSourceCommit ?? "unreadable"}
            />
            <Row
              label="Packs checkout"
              value={engine.packsSourceCommit ?? "unreadable"}
            />
            {/* Declared, for the same reason the web digest is: the engine runs
                as an init container and no process can read its image. */}
            <Row
              label="Image digest"
              value={engine.imageDigest ?? "declared by the deployment: none"}
              note="What the deployment says it staged. The authoritative read is the kubelet's."
            />
            <Row label="Agreement with its image" value={engine.agreement} />
          </>
        ) : (
          <Row
            label="Mounted"
            value="no engine is mounted on this deployment"
          />
        )}
      </Section>

      <Section title="Serving Brain">
        <Row
          label="Source"
          value={
            brain.source === "latest-admission"
              ? "last recorded admission"
              : brain.source === "none-recorded"
                ? "no candidate has been admitted here yet"
                : "the evidence store could not be read"
          }
        />
        <Row
          label="Brain commit"
          value={brain.servingBrainCommit ?? "unknown"}
        />
        <Row
          label="Brain image"
          value={brain.servingBrainImageDigest ?? "unknown"}
        />
        <Row label="Bound to" value={brain.apiTarget ?? "not configured"} />
      </Section>
    </>
  );
}

function Section({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <section className="rounded-md border border-border bg-card p-3">
      <h3 className="text-sm font-semibold text-foreground">{title}</h3>
      <dl className="mt-2 space-y-2">{children}</dl>
    </section>
  );
}

function Row({
  label,
  value,
  note,
}: {
  label: string;
  value: string;
  note?: string;
}) {
  return (
    <div className="grid gap-0.5 sm:grid-cols-[10rem_minmax(0,1fr)] sm:gap-3">
      <dt className="text-xs font-medium text-muted-foreground sm:pt-0.5">
        {label}
      </dt>
      <dd className="min-w-0">
        {/* `break-all`: a commit id and a digest are long unbroken tokens, and a
            support panel that cuts one off has withheld the answer. */}
        <span className="block break-all font-mono text-xs text-foreground">
          {value}
        </span>
        {note ? (
          <span className="block text-xs text-muted-foreground">{note}</span>
        ) : null}
      </dd>
    </div>
  );
}
